import { defaultConfig, defineConfig, defineTokens } from '@chakra-ui/react';

import { globalCss } from './misc/global-css';
import { keyframes } from './misc/keyframes';
import { textStyles } from './misc/text-styles';
import { accommodationItemSlotRecipe } from './recipes/accommodation-item';
import { accommodationTileSlotRecipe } from './recipes/accommodation-tile';
import { accordionSlotRecipe } from './recipes/accordion';
import { accordionMenuItemSlotRecipe } from './recipes/accordion-menu-item';
import { alertSlotRecipe } from './recipes/alert';
import { autocompleteSlotRecipe } from './recipes/autocomplete';
import { autocompleteItemSlotRecipe } from './recipes/autocomplete-item';
import { avatarSlotRecipe } from './recipes/avatar';
import { averageRatingSlotRecipe } from './recipes/average-rating';
import { badgeRecipe } from './recipes/badge';
import { buttonRecipe } from './recipes/button';
import { checkboxSlotRecipe } from './recipes/checkbox';
import { cssTooltipSlotRecipe } from './recipes/css-tooltip';
import { dialogSlotRecipe } from './recipes/dialog';
import { drawerSlotRecipe } from './recipes/drawer';
import { expandableMenuItemSlotRecipe } from './recipes/expandable-menu-item';
import { featuredCountryItemSlotRecipe } from './recipes/featured-country-item';
import { featuredImageWrapperSlotRecipe } from './recipes/featured-image-wrapper';
import { fieldSlotRecipe } from './recipes/field';
import { footerSlotRecipe } from './recipes/footer';
import { guestsPickerSlotRecipe } from './recipes/guests-picker';
import { headingRecipe } from './recipes/heading';
import { heroSlotRecipe } from './recipes/hero';
import { iconSlotRecipe } from './recipes/icon';
import { inputRecipe } from './recipes/input';
import { linkRecipe } from './recipes/link';
import { listSlotRecipe } from './recipes/list';
import { mapAdvancedMarkerSlotRecipe } from './recipes/map-advanced-marker';
import { mapPopoverSlotRecipe } from './recipes/map-popover';
import { menuSlotRecipe } from './recipes/menu';
import { mobileNumberInputSlotRecipe } from './recipes/mobile-number-input';
import { nativeSelectSlotRecipe } from './recipes/native-select';
import { navigationSlotRecipe } from './recipes/navigation';
import { newsletterSlotRecipe } from './recipes/newsletter';
import { popoverSlotRecipe } from './recipes/popover';
import { rangePickerSlotRecipe } from './recipes/range-picker';
import { rangePickerDrawerSlotRecipe } from './recipes/range-picker-drawer';
import { ratingSlotRecipe } from './recipes/rating';
import { searchEngineDesktopSlotRecipe } from './recipes/search-engine-desktop';
import { searchEngineInstantReservationCheckboxSlotRecipe } from './recipes/search-engine-instant-reservation-checkbox';
import { searchEngineMobileSlotRecipe } from './recipes/search-engine-mobile';
import { searchEngineMobileDrawerSlotRecipe } from './recipes/search-engine-mobile-drawer';
import { searchLocationSlotRecipe } from './recipes/search-location';
import { segmentGroupRecipe } from './recipes/segment-group';
import { seoLinksSlotRecipe } from './recipes/seo-links';
import { separatorRecipe } from './recipes/separator';
import { skeletonRecipe } from './recipes/skeleton';
import { spinnerRecipe } from './recipes/spinner';
import { supportSlotRecipe } from './recipes/support';
import { tagSlotRecipe } from './recipes/tag';
import { toastSlotRecipe } from './recipes/toast';
import { colors as semanticColors } from './semantic-tokens/colors';
import { shadows } from './semantic-tokens/shadows';
import { animations } from './tokens/animations';
import { colors } from './tokens/colors';
import { fontSizes } from './tokens/font-sizes';
import { fonts } from './tokens/fonts';
import { radii } from './tokens/radii';
import { sizes } from './tokens/sizes';
import { spacing } from './tokens/spacing';
import { zIndex } from './tokens/z-index';

export const raspberryBaseTheme = defineConfig({
    globalCss,

    theme: {
        breakpoints: {
            // for some reason `base` breakpoint is not defined by default, which causes issues with breakpoint-based hooks,
            // we need to add it manually
            base: '0rem', // 0px
            ...defaultConfig?.theme?.breakpoints,
        },
        tokens: defineTokens({
            animations,
            colors,
            spacing,
            radii,
            sizes,
            fonts,
            fontSizes,
            zIndex,
        }),

        semanticTokens: {
            colors: semanticColors,
            shadows,
        },

        keyframes,

        textStyles,

        // for defining component recipes
        recipes: {
            badge: badgeRecipe,
            button: buttonRecipe,
            heading: headingRecipe,
            input: inputRecipe,
            link: linkRecipe,
            separator: separatorRecipe,
            skeleton: skeletonRecipe,
            spinner: spinnerRecipe,
        },

        // for defining component slot recipes
        slotRecipes: {
            accommodationItem: accommodationItemSlotRecipe,
            accommodationTile: accommodationTileSlotRecipe,
            accordion: accordionSlotRecipe,
            accordionMenuItem: accordionMenuItemSlotRecipe,
            alert: alertSlotRecipe,
            autocomplete: autocompleteSlotRecipe,
            autocompleteItem: autocompleteItemSlotRecipe,
            avatar: avatarSlotRecipe,
            averageRating: averageRatingSlotRecipe,
            checkbox: checkboxSlotRecipe,
            cssTooltip: cssTooltipSlotRecipe,
            dialog: dialogSlotRecipe,
            drawer: drawerSlotRecipe,
            field: fieldSlotRecipe,
            expandableMenuItem: expandableMenuItemSlotRecipe,
            featuredCountryItem: featuredCountryItemSlotRecipe,
            featuredImageWrapper: featuredImageWrapperSlotRecipe,
            footer: footerSlotRecipe,
            guestsPicker: guestsPickerSlotRecipe,
            guestsPickerSelect: guestsPickerSlotRecipe,
            hero: heroSlotRecipe,
            icon: iconSlotRecipe,
            list: listSlotRecipe,
            mapAdvancedMarker: mapAdvancedMarkerSlotRecipe,
            mapPopover: mapPopoverSlotRecipe,
            menu: menuSlotRecipe,
            mobileNumberInput: mobileNumberInputSlotRecipe,
            nativeSelect: nativeSelectSlotRecipe,
            navigation: navigationSlotRecipe,
            newsletter: newsletterSlotRecipe,
            popover: popoverSlotRecipe,
            rangePicker: rangePickerSlotRecipe,
            rangePickerDrawer: rangePickerDrawerSlotRecipe,
            rating: ratingSlotRecipe,
            searchEngineDesktop: searchEngineDesktopSlotRecipe,
            searchEngineInstantReservationCheckbox:
                searchEngineInstantReservationCheckboxSlotRecipe,
            searchEngineMobile: searchEngineMobileSlotRecipe,
            searchEngineMobileDrawer: searchEngineMobileDrawerSlotRecipe,
            searchLocation: searchLocationSlotRecipe,
            segmentGroup: segmentGroupRecipe,
            seoLinks: seoLinksSlotRecipe,
            support: supportSlotRecipe,
            tag: tagSlotRecipe,
            toast: toastSlotRecipe,
        },
    },
});
